import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import { Subscription, forkJoin, interval } from 'rxjs';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import {
	CampaignEffortService,
	CallerTimeRow,
	DispoRow,
} from 'src/app/services/campaign-effort/campaign-effort.service';

dayjs.extend(utc);

interface DispoBucket {
	label: string;
	count: number;
	percent: number;
	color: string;
}

interface CampaignBreakdownRow {
	campaign: string;
	dispos: number;
	leads: number;
	convPct: number;
}

interface HourBucket {
	label: string;
	dispos: number;
	leads: number;
}

const REFRESH_SECONDS = 30;

/**
 * Individual drill-down for a caller selected from Caller Tracking (../caller-tracking.component.ts).
 * Session/header fields (login time, last action, idle) come from the same
 * `dashboard/users_activity` + `ringcentral/get_dashboard_stats` calls the list already uses, so the
 * numbers match what the user just saw in the "View →" row. Dispo-level breakdown (per-campaign,
 * per-hour, activity timeline) comes from CampaignEffortService, filtered to this caller — no new
 * backend endpoints.
 */
@Component({
	selector: 'app-caller-tracking-detail',
	templateUrl: './caller-tracking-detail.component.html',
	styleUrls: ['./caller-tracking-detail.component.scss'],
})
export class CallerTrackingDetailComponent implements OnInit, OnDestroy {
	loading = true;
	notFound = false;

	callerId: string | null = null;
	callerName = '';
	caller: any = null; // row from dashboard/users_activity — session/header info

	fromDate = '';
	toDate = '';

	dispos: DispoRow[] = [];
	callerTimeRow: CallerTimeRow | null = null;

	kpi = { dials: 0, talkMs: 0, dispos: 0, leads: 0, campaigns: 0, disposRate: 0 };

	dispoBreakdown: DispoBucket[] = [];
	campaignBreakdown: CampaignBreakdownRow[] = [];
	timeline: DispoRow[] = [];
	hourBuckets: HourBucket[] = [];

	hourChart: { series: { name: string; data: number[] }[]; categories: string[] } = { series: [], categories: [] };

	private refreshSub: Subscription | null = null;

	constructor(
		private route: ActivatedRoute,
		private dashboardSrv: DashboardService,
		private effortSrv: CampaignEffortService,
		public utils: UtilsService,
	) {}

	ngOnInit(): void {
		this.callerId = this.route.snapshot.paramMap.get('id');
		this.caller = history.state?.caller ?? null;

		this.fromDate = dayjs().format('YYYY-MM-DD') + ' 00:00:00';
		this.toDate = dayjs().format('YYYY-MM-DD') + ' 23:59:59';

		this.load();
		this.refreshSub = interval(REFRESH_SECONDS * 1000).subscribe(() => this.load(true));
	}

	ngOnDestroy(): void {
		this.refreshSub?.unsubscribe();
		this.refreshSub = null;
	}

	/**
	 * Same two calls as the Caller Tracking list (getUsersActivity + getDashboardStats) so
	 * Dials/Talk Time/Appointments here match what the user just saw on the row they clicked.
	 */
	private load(silent = false): void {
		if (!silent) this.loading = true;

		const payload = {
			timezone: this.utils.getUserTimeZone(),
			start_date: dayjs().subtract(5, 'hour').format('YYYY-MM-DD HH:mm:ss'),
			end_date: dayjs().add(1, 'hour').format('YYYY-MM-DD HH:mm:ss'),
		};

		forkJoin({
			activity: this.dashboardSrv.getUsersActivity(payload),
			stats: this.dashboardSrv.getDashboardStats(),
		}).subscribe(({ activity, stats }: any) => {
			const match = (activity.body?.actions ?? []).find((u: any) => String(u.user_id) === String(this.callerId));
			const dialsByAgent = stats?.body?.dialsByAgent ?? {};
			const talkTimeByAgent = stats?.body?.talkTimeByAgent ?? {};
			const appointmentsByUser = stats?.body?.appointmentsByUser ?? {};
			const lastCallByAgent = stats?.body?.lastCallByAgent ?? {};
			// Normalize keys to avoid whitespace/casing mismatches — ports HomeComponent's applyStatsToUsers().
			const normalizedLastCall: { [key: string]: any } = {};
			Object.keys(lastCallByAgent).forEach((key) => {
				normalizedLastCall[key.trim().toLowerCase()] = lastCallByAgent[key];
			});

			if (match) {
				this.caller = {
					...match,
					date_time: dayjs.utc(match.date_time).toDate(),
					dials: dialsByAgent[match.full_name] || 0,
					// talkTimeByAgent is in seconds (matches HomeComponent/caller-tracking.component.ts formatTime()).
					talkTime: this.formatSeconds(talkTimeByAgent[match.full_name] || 0),
					appointments: appointmentsByUser[String(match.user_id)] || 0,
					lastCall: normalizedLastCall[match.full_name?.trim().toLowerCase()] || null,
				};
			} else if (!this.caller) {
				this.notFound = true;
			}
			this.callerName = this.caller?.full_name ?? '';
			if (this.callerName) this.loadEffort();
			else this.loading = false;
		});
	}

	private loadEffort(): void {
		this.effortSrv.loadRange(this.fromDate, this.toDate).subscribe({
			next: (data) => {
				this.dispos = data.dispos.filter((d) => d.caller_name === this.callerName);
				this.callerTimeRow = data.callerTimes.find((c) => c.name === this.callerName) ?? null;
				this.computeKpis();
				this.computeDispoBreakdown();
				this.computeCampaignBreakdown();
				this.computeHourBuckets();
				this.timeline = this.dispos.slice().sort((a, b) => Number(b.id) - Number(a.id));
				this.loading = false;
			},
			error: () => {
				this.loading = false;
			},
		});
	}

	private computeKpis(): void {
		const dials = this.n(this.caller?.dials) || this.n(this.callerTimeRow?.totalCalls);
		const dispos = this.n(this.callerTimeRow?.totalDispos);
		const leads = this.n(this.callerTimeRow?.green);
		const campaigns = new Set(this.dispos.map((d) => d.campaign_name)).size;
		this.kpi = {
			dials,
			talkMs: this.n(this.callerTimeRow?.timeInCalls),
			dispos,
			leads,
			campaigns,
			disposRate: dials ? (dispos / dials) * 100 : 0,
		};
	}

	private computeDispoBreakdown(): void {
		const c = this.callerTimeRow;
		const raw: { label: string; count: number; color: string }[] = [
			{ label: 'No Answer', count: this.n(c?.blue), color: '#40A0FC' },
			{ label: 'Callback / Hot', count: this.n(c?.yellow), color: '#FEBC4A' },
			{ label: 'Lead Set', count: this.n(c?.green), color: '#50E7A6' },
			{ label: 'Bad Phone', count: this.n(c?.black), color: '#3F475D' },
			{ label: 'Other', count: this.n(c?.other), color: '#FE4560' },
		];
		const total = raw.reduce((s, r) => s + r.count, 0);
		this.dispoBreakdown = raw.map((r) => ({ ...r, percent: total ? (r.count / total) * 100 : 0 }));
	}

	private computeCampaignBreakdown(): void {
		const groups = new Map<string, DispoRow[]>();
		for (const d of this.dispos) {
			const g = groups.get(d.campaign_name) ?? [];
			g.push(d);
			groups.set(d.campaign_name, g);
		}
		const rows: CampaignBreakdownRow[] = [];
		groups.forEach((list, campaign) => {
			const leads = list.filter((d) => this.effortSrv.isLead(d.dispo)).length;
			rows.push({
				campaign: campaign || '—',
				dispos: list.length,
				leads,
				convPct: list.length ? (leads / list.length) * 100 : 0,
			});
		});
		this.campaignBreakdown = rows.sort((a, b) => b.dispos - a.dispos);
	}

	private computeHourBuckets(): void {
		const byHour = new Map<number, { dispos: number; leads: number }>();
		for (const d of this.dispos) {
			const hour = dayjs.utc(d.date).local().hour();
			const bucket = byHour.get(hour) ?? { dispos: 0, leads: 0 };
			bucket.dispos += 1;
			if (this.effortSrv.isLead(d.dispo)) bucket.leads += 1;
			byHour.set(hour, bucket);
		}
		const hours = Array.from(byHour.keys()).sort((a, b) => a - b);
		this.hourBuckets = hours.map((h) => ({
			label: dayjs().hour(h).minute(0).format('ha'),
			...byHour.get(h),
		}));
		this.hourChart = {
			categories: this.hourBuckets.map((b) => b.label),
			series: [
				{ name: 'Dispos', data: this.hourBuckets.map((b) => b.dispos) },
				{ name: 'Leads', data: this.hourBuckets.map((b) => b.leads) },
			],
		};
	}

	// ── Display helpers ─────────────────────────────────────────────────────
	talk(ms: number): string {
		return ms ? this.utils.formatMiliSeconds(ms) : '—';
	}

	private formatSeconds(seconds: number): string {
		if (!seconds) return '—';
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
	}

	get idleMinutes(): number {
		return this.caller?.date_time ? dayjs().diff(dayjs(this.caller.date_time), 'minute') : 0;
	}

	get statusLabel(): string {
		const m = this.idleMinutes;
		if (m <= 30) return 'Active';
		if (m <= 59) return 'Idle';
		return 'Away';
	}

	get statusClass(): string {
		const m = this.idleMinutes;
		if (m <= 30) return 'idle-green';
		if (m <= 59) return 'idle-orange';
		return 'idle-red';
	}

	initials(name: string): string {
		if (!name) return '';
		const parts = name.trim().split(' ');
		const first = parts[0]?.charAt(0) || '';
		const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
		return (first + last).toUpperCase();
	}

	badgeClass(dispo: string): string {
		switch (this.effortSrv.bucketOf(dispo)) {
			case 'green': return 'bg-success';
			case 'yellow': return 'bg-warning';
			case 'blue': return 'bg-primary';
			case 'black': return 'bg-dark';
			default: return 'bg-secondary';
		}
	}

	private n(v: number | string | undefined): number {
		const x = parseInt(v as string, 10);
		return isNaN(x) ? 0 : x;
	}
}
